/**
 * Planned — Aim 1 (Building Block Libraries) Action Definitions
 *
 * Actions: dissect-building-block, annotate-reactions, prepare-vendor-library, validate-dissection
 *
 * All actions in this file have status: 'planned' and null execution references.
 *
 * @module gibbs/registry/actions/planned-aim1
 */

import { WorkflowTypes as T } from '../types';
import { param, pre, post } from '../schema';

// ─── dissect-building-block ─────────────────────────────────────────────────

export const dissectBuildingBlock = {
    id: 'dissect-building-block',
    name: 'Dissect Building Block into Fragments',
    category: 'Planned — Aim 1',
    description: 'Automatically break a chemical building block from a vendor library into simulation-ready fragments. Uses rotatable bond division and reaction-site division. Ensures fragments are chemically representative of the building block\'s behavior after reaction.',
    inputs: [
        param('buildingBlock', T.MolData, 'Building block structure (SMILES/SDF from vendor)'),
        param('reactions', 'object[]', 'Applicable reactions for the building block'),
    ],
    outputs: [
        param('fragments', 'object[]', 'Simulation-ready fragments'),
        param('associations', 'object[]', 'Building block to fragment relationships with geometric data'),
        param('attachmentPoints', 'object[]', 'Identified attachment points per fragment'),
    ],
    parameters: [
        param('divisionMethod', 'string', 'Fragment division method', false, { defaultValue: 'both', options: ['rotatable_bond', 'reaction_site', 'both'] }),
        param('chargeThreshold', 'number', 'Max allowed electron distribution change', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Building block must be loaded'),
    ],
    postconditions: [
        post('fragment_data_available', 'Dissected fragments are available for simulation'),
    ],
    runtime: 'short',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── annotate-reactions ─────────────────────────────────────────────────────

export const annotateReactions = {
    id: 'annotate-reactions',
    name: 'Annotate Building Block Reactions',
    category: 'Planned — Aim 1',
    description: 'Identify which chemical reactions from a library can apply to each building block\'s attachment points. Annotates each attachment point with compatible reaction types.',
    inputs: [
        param('buildingBlock', T.MolData, 'Building block structure'),
        param('reactionLibrary', 'object[]', 'Available reaction definitions (e.g., Suzuki coupling, amide bond formation, click chemistry)'),
    ],
    outputs: [
        param('annotations', 'object[]', 'Per-attachment-point reaction compatibility'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Building block must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── prepare-vendor-library ─────────────────────────────────────────────────

export const prepareVendorLibrary = {
    id: 'prepare-vendor-library',
    name: 'Prepare Vendor Building Block Library',
    category: 'Planned — Aim 1',
    description: 'Process an entire vendor building block library (e.g., eMolecules 4.5M+ or Enamine 300K+) through the dissection and annotation pipeline. Populates the PostgreSQL database with fragments, building blocks, reactions, and their relationships.',
    inputs: [
        param('vendorLibrary', 'object', 'Library specification (vendor, format, file path)'),
        param('reactionLibrary', 'object[]', 'Reaction definitions to annotate with'),
    ],
    outputs: [
        param('database', 'object', 'Populated fragment-building block database with reaction annotations'),
        param('stats', 'object', 'Coverage report (% processed, fragments created, reactions matched)'),
    ],
    parameters: [
        param('vendor', 'string', 'Vendor source', false, { options: ['emolecules', 'enamine'] }),
        param('conformerThreshold', 'number', 'Min conformers to skip dissection', false),
    ],
    dependsOn: ['dissect-building-block', 'annotate-reactions'],
    preconditions: [],
    postconditions: [],
    runtime: 'background',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── validate-dissection ────────────────────────────────────────────────────

export const validateDissection = {
    id: 'validate-dissection',
    name: 'Validate Fragment Dissection',
    category: 'Planned — Aim 1',
    description: 'Run ab initio quantum calculations to confirm that a fragment dissection preserves electronic distribution within acceptable thresholds. Spot-check validation for dissection quality.',
    inputs: [
        param('buildingBlock', T.MolData, 'Original building block'),
        param('fragments', 'object[]', 'Dissected fragments'),
    ],
    outputs: [
        param('valid', 'boolean', 'Whether dissection is chemically acceptable'),
        param('chargeDeviation', 'number', 'Electron distribution change'),
    ],
    parameters: [
        param('threshold', 'number', 'Max acceptable deviation', false),
    ],
    dependsOn: ['dissect-building-block'],
    preconditions: [
        pre('fragment_data_available', 'Dissected fragment data must be available'),
    ],
    postconditions: [],
    runtime: 'long',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    dissectBuildingBlock,
    annotateReactions,
    prepareVendorLibrary,
    validateDissection,
];
